/**
 * CharacterCreateScreen — 角色自定义创建表单
 *
 * 玩家为男女双方各创建一个角色，支持手动输入和随机生成。
 */
import React, { useState, useCallback } from 'react';
import { Shuffle, ChevronRight, User, Heart, BookOpen } from 'lucide-react';
import type { CharacterInput, Gender, LoveView, IndependenceLevel, AssetLevel } from '@shared/types/character';
import {
  JOB_OPTIONS,
  PERSONALITY_TAGS,
  INTEREST_TAGS,
  RANDOM_NAMES_MALE,
  RANDOM_NAMES_FEMALE,
  CHARACTER_PRESETS,
} from '@shared/constants/characterOptions';
import TagSelector from './TagSelector';

interface CharacterCreateScreenProps {
  onComplete: (inputA: CharacterInput, inputB: CharacterInput) => void;
  isGenerating: boolean;
}

const LOVE_VIEWS: LoveView[] = ['传统', '开放', '浪漫', '务实', '随缘', '恐婚'];
const INDEPENDENCE_LEVELS: IndependenceLevel[] = ['高', '中', '低'];
const ASSET_LEVELS: AssetLevel[] = ['基础生存', '小资', '中资', '财务自由', '顶级富豪'];

function randomPick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomPickN<T>(arr: T[], n: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

function randomAge(): number {
  return 22 + Math.floor(Math.random() * 28);
}

function createRandomInput(gender: Gender): CharacterInput {
  const names = gender === 'male' ? RANDOM_NAMES_MALE : RANDOM_NAMES_FEMALE;
  return {
    name: randomPick(names),
    age: randomAge(),
    gender,
    job: randomPick(JOB_OPTIONS),
    personality: randomPickN(PERSONALITY_TAGS, 2 + Math.floor(Math.random() * 2)),
    interests: randomPickN(INTEREST_TAGS, 2 + Math.floor(Math.random() * 2)),
    loveView: randomPick(LOVE_VIEWS),
    independence: randomPick(INDEPENDENCE_LEVELS),
    asset: randomPick(ASSET_LEVELS),
  };
}

function createEmptyInput(gender: Gender): CharacterInput {
  return {
    name: '',
    age: gender === 'male' ? 28 : 26,
    gender,
    job: '',
    personality: [],
    interests: [],
    loveView: '随缘',
    independence: '中',
    asset: '小资',
  };
}

interface CharacterFormProps {
  label: string;
  gender: Gender;
  input: CharacterInput;
  onChange: (input: CharacterInput) => void;
  onRandomize: () => void;
}

const CharacterForm: React.FC<CharacterFormProps> = ({ label, gender, input, onChange, onRandomize }) => {
  const update = <K extends keyof CharacterInput>(key: K, value: CharacterInput[K]) => {
    onChange({ ...input, [key]: value });
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E5E0D8]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <User size={16} className={gender === 'male' ? 'text-blue-500' : 'text-pink-500'} />
          <span className="font-bold text-sm">{label}</span>
        </div>
        <button
          onClick={onRandomize}
          className="flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-coral-500 transition-colors"
        >
          <Shuffle size={12} />
          随机生成
        </button>
      </div>

      <div className="space-y-3">
        {/* 姓名 + 年龄 */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="姓名"
            value={input.name}
            onChange={e => update('name', e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] text-sm focus:outline-none focus:border-coral-400"
            maxLength={10}
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={input.age}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') { update('age', '' as unknown as number); return; }
                const n = parseInt(raw);
                if (!isNaN(n)) update('age', n);
              }}
              onBlur={() => {
                const clamped = Math.max(18, Math.min(60, Number(input.age) || 22));
                update('age', clamped);
              }}
              className="w-16 px-2 py-2 rounded-lg border border-[#E5E0D8] text-sm text-center focus:outline-none focus:border-coral-400"
              min={18}
              max={60}
            />
            <span className="text-xs text-[#9CA3AF]">岁</span>
          </div>
        </div>

        {/* 职业 */}
        <select
          value={input.job}
          onChange={e => update('job', e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] text-sm focus:outline-none focus:border-coral-400 bg-white"
        >
          <option value="">选择职业...</option>
          {JOB_OPTIONS.map(j => <option key={j} value={j}>{j}</option>)}
        </select>

        {/* 性格标签 */}
        <div>
          <label className="text-xs text-[#6B7280] mb-1 block">性格标签（2-3个）</label>
          <TagSelector
            options={PERSONALITY_TAGS}
            selected={input.personality}
            onChange={tags => update('personality', tags)}
            max={3}
          />
        </div>

        {/* 兴趣标签 */}
        <div>
          <label className="text-xs text-[#6B7280] mb-1 block">兴趣爱好（2-3个）</label>
          <TagSelector
            options={INTEREST_TAGS}
            selected={input.interests}
            onChange={tags => update('interests', tags)}
            max={3}
          />
        </div>

        {/* 婚恋观 */}
        <div>
          <label className="text-xs text-[#6B7280] mb-1 block">婚恋观</label>
          <div className="flex flex-wrap gap-1.5">
            {LOVE_VIEWS.map(v => (
              <button
                key={v}
                onClick={() => update('loveView', v)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                  input.loveView === v
                    ? 'bg-coral-500 text-white'
                    : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* 独立性 + 资产 */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-[#6B7280] mb-1 block">独立性</label>
            <div className="flex gap-1">
              {INDEPENDENCE_LEVELS.map(l => (
                <button
                  key={l}
                  onClick={() => update('independence', l)}
                  className={`flex-1 py-1 rounded-lg text-xs transition-colors ${
                    input.independence === l
                      ? 'bg-coral-500 text-white'
                      : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-[#6B7280] mb-1 block">资产水平</label>
            <select
              value={input.asset}
              onChange={e => update('asset', e.target.value as AssetLevel)}
              className="w-full px-2 py-1 rounded-lg border border-[#E5E0D8] text-xs focus:outline-none focus:border-coral-400 bg-white"
            >
              {ASSET_LEVELS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

const CharacterCreateScreen: React.FC<CharacterCreateScreenProps> = ({ onComplete, isGenerating }) => {
  const [inputA, setInputA] = useState<CharacterInput>(createEmptyInput('male'));
  const [inputB, setInputB] = useState<CharacterInput>(createEmptyInput('female'));

  const randomizeA = useCallback(() => setInputA(createRandomInput('male')), []);
  const randomizeB = useCallback(() => setInputB(createRandomInput('female')), []);
  const randomizeAll = useCallback(() => {
    setInputA(createRandomInput('male'));
    setInputB(createRandomInput('female'));
  }, []);

  const isValid = inputA.name && inputA.job && inputA.personality.length >= 2 && inputA.interests.length >= 2
    && inputB.name && inputB.job && inputB.personality.length >= 2 && inputB.interests.length >= 2;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-coral-100 p-4 pb-24">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-4 pt-2">
          <h2 className="text-xl font-bold text-[#2D2D2D]">创建角色</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">为这对相亲男女设定基本信息</p>
        </div>

        {/* 一键随机 */}
        <button
          onClick={randomizeAll}
          className="w-full mb-4 py-2 rounded-xl bg-white border border-dashed border-[#E5E0D8] text-sm text-[#6B7280] hover:border-coral-400 hover:text-coral-500 transition-colors flex items-center justify-center gap-2"
        >
          <Shuffle size={14} />
          一键随机生成两位角色
        </button>

        {/* 预设角色 */}
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-2">
            <BookOpen size={13} className="text-[#9CA3AF]" />
            <span className="text-xs text-[#9CA3AF]">预设人物卡（点击填入对应表单）</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {CHARACTER_PRESETS.map(preset => (
              <button
                key={preset.label}
                onClick={() => {
                  const setter = preset.input.gender === 'male' ? setInputA : setInputB;
                  setter(preset.input);
                }}
                className="flex-shrink-0 px-3 py-2 rounded-xl bg-white border border-[#E5E0D8] hover:border-coral-400 transition-colors text-left"
              >
                <div className="text-xs font-medium text-[#2D2D2D] whitespace-nowrap">{preset.label}</div>
                <div className="text-[10px] text-[#9CA3AF] mt-0.5 whitespace-nowrap">{preset.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 双角色表单 */}
        <div className="space-y-4">
          <CharacterForm
            label="男方"
            gender="male"
            input={inputA}
            onChange={setInputA}
            onRandomize={randomizeA}
          />
          <div className="flex justify-center">
            <Heart size={20} className="text-coral-300" />
          </div>
          <CharacterForm
            label="女方"
            gender="female"
            input={inputB}
            onChange={setInputB}
            onRandomize={randomizeB}
          />
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-cream via-cream to-transparent">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => isValid && onComplete(inputA, inputB)}
            disabled={!isValid || isGenerating}
            className={`w-full py-3.5 rounded-full font-bold text-base flex items-center justify-center gap-2 transition-all ${
              isValid && !isGenerating
                ? 'bg-gradient-to-r from-coral-500 to-amber-accent text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                AI 正在丰富角色...
              </>
            ) : (
              <>
                下一步：选择亲友团
                <ChevronRight size={16} />
              </>
            )}
          </button>
          {!isValid && !isGenerating && (
            <p className="text-center text-xs text-[#9CA3AF] mt-2">
              请填写姓名、职业，并选择至少2个性格和兴趣标签
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterCreateScreen;
